
import { useState, useEffect } from 'react';
import { Instagram } from 'lucide-react';
import { useInView } from '../utils/animations';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { useToast } from "@/components/ui/use-toast";
import { useIsMobile } from '../hooks/use-mobile';

interface InstagramPost {
  id: string;
  media_url: string;
  media_type?: string;
  caption?: string;
  permalink: string;
  timestamp: string;
  thumbnail_url?: string;
}

interface FormattedInstagramPost {
  id: string;
  imageUrl: string;
  caption: string;
  likes: number;
  link: string;
  isReel: boolean;
}

const fallbackPosts = [
  {
    id: '1',
    imageUrl: '/lovable-uploads/1da4982d-cf51-443d-a53b-c88707f47a47.png',
    caption: 'Wspaniały wieczór z @jankowalski i @annakowalska. #portrait #photography',
    likes: 124,
    link: 'https://www.instagram.com/p/C2MjmozN2eG/',
    isReel: false
  },
  {
    id: '2',
    imageUrl: '/lovable-uploads/f042cc68-c11c-4bfa-9997-0bc0dd0af194.png',
    caption: 'Sesja w Parku Miejskim, pełna emocji. #photooftheday #portrait',
    likes: 87,
    link: 'https://www.instagram.com/p/C2MjmozN2eG/',
    isReel: false
  },
  {
    id: '3',
    imageUrl: '/lovable-uploads/22de1b96-c8ac-4016-b67f-9655b47d7a0a.png',
    caption: 'Wspomnienia, które pozostaną na zawsze. #familyphotography',
    likes: 245,
    link: 'https://www.instagram.com/p/C2MjmozN2eG/',
    isReel: true
  },
  {
    id: '4',
    imageUrl: '/lovable-uploads/f1c7fccc-df0a-462d-830a-ab9b597ed526.png',
    caption: 'Urodziny w stylu retro. #birthdayparty #photobooth',
    likes: 110,
    link: 'https://www.instagram.com/p/C2MjmozN2eG/',
    isReel: false
  },
  {
    id: '5',
    imageUrl: '/lovable-uploads/28ea1852-4e58-4daf-8b79-984f3d3b88ae.png',
    caption: 'Dzisiaj na kolejnym evencie biznesowym. #eventphoto #corporate',
    likes: 68,
    link: 'https://www.instagram.com/p/C2MjmozN2eG/',
    isReel: true
  },
  {
    id: '6',
    imageUrl: '/lovable-uploads/2cfc07fd-6e1c-48f5-b85a-0870b6be7d76.png',
    caption: 'Wesele Agnieszki i Marka - dziękujemy za zaufanie! #wedding #love',
    likes: 189,
    link: 'https://www.instagram.com/p/C2MjmozN2eG/',
    isReel: false
  }
];

// Image to use for the uploaded example
const uploadedExampleImage = '/lovable-uploads/9dea227b-2922-43c6-8bc2-e9592e93b115.png';

// Using a test token - should be replaced with actual token in production
const INSTAGRAM_TOKEN = 'IGAAXfbNbCll5BZAE9KVUpIT2g0U1dqUnJIRWV1VVJyakUwVGxTRF9RX1R4QllzdkZA2aEVqTHkzVDhoSlpUbGVRZATJWS0x1MlZAfUXhMVmhjY293SFA2enhHVUJKT1NUclpVc25QUnVhZAEM4cFd0QmJoRWU5RlExV1FYVFlDX2FBVQZDZD';

const InstagramFeed = () => {
  const { toast } = useToast();
  const [titleRef, titleInView] = useInView({ threshold: 0.1 });
  const [feedRef, feedInView] = useInView({ threshold: 0.1 });
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState<FormattedInstagramPost[]>([]);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    fetchInstagramPosts();
  }, []);

  const fetchInstagramPosts = async () => {
    try {
      const response = await fetch(
        `https://graph.instagram.com/me/media?fields=id,caption,media_url,permalink,timestamp,media_type,thumbnail_url&access_token=${INSTAGRAM_TOKEN}`
      );
      
      if (!response.ok) {
        console.error('Instagram API response:', await response.text());
        throw new Error('Błąd podczas pobierania danych z Instagrama');
      }
      
      const data = await response.json();
      
      if (data && data.data && Array.isArray(data.data)) {
        const formattedPosts: FormattedInstagramPost[] = data.data
          .slice(0, 6)
          .map((post: InstagramPost) => {
            // Handle different media types (REEL vs IMAGE)
            const isReel = post.media_type === 'VIDEO';
            const imageUrl = isReel && post.thumbnail_url ? post.thumbnail_url : post.media_url;
            
            return {
              id: post.id,
              imageUrl: imageUrl || uploadedExampleImage,
              caption: post.caption || 'Czułe Klisze #fotografia',
              likes: Math.floor(Math.random() * 200) + 50,
              link: post.permalink,
              isReel: isReel
            };
          });
        
        setPosts(formattedPosts);
      } else {
        // Use fallback posts with the example image
        const updatedFallbackPosts = [...fallbackPosts];
        // Update at least one post to use the uploaded example image
        updatedFallbackPosts[2] = {
          ...updatedFallbackPosts[2],
          imageUrl: uploadedExampleImage,
          isReel: true
        };
        
        setPosts(updatedFallbackPosts);
      }
    } catch (error) {
      console.error('Błąd pobierania danych z Instagrama:', error);
      toast({
        title: "Informacja",
        description: "Używamy przykładowych zdjęć, ponieważ nie udało się połączyć z Instagramem.",
        variant: "default",
      });
      
      // Use fallback posts with the example image
      const updatedFallbackPosts = [...fallbackPosts];
      // Update at least one post to use the uploaded example image
      updatedFallbackPosts[2] = {
        ...updatedFallbackPosts[2],
        imageUrl: uploadedExampleImage,
        isReel: true
      };
      
      setPosts(updatedFallbackPosts);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="instagram" className="section bg-elegant-50">
      <div className="container-custom">
        <div 
          ref={titleRef as any}
          className={`text-center max-w-2xl mx-auto mb-8 ${
            titleInView ? 'opacity-100 translate-y-0 transition-all duration-700' : 'opacity-0 translate-y-10 transition-all duration-700'
          }`}
        >
          <h2 className={`section-title ${isMobile ? 'text-2xl' : ''}`}>Śledź Nas na Instagramie</h2>
          <p className={`section-subtitle ${isMobile ? 'text-xs' : ''}`}>
            Zobacz nasze najnowsze realizacje i kulisy sesji
          </p>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-elegant-900"></div>
          </div>
        ) : (
          <div 
            ref={feedRef as any}
            className={`grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1 md:gap-3 ${
              feedInView ? 'opacity-100 translate-y-0 transition-all duration-700 delay-200' : 'opacity-0 translate-y-10 transition-all duration-700'
            }`}
          >
            {posts.map((post, index) => (
              <a 
                key={post.id}
                href={post.link}
                target="_blank"
                rel="noopener noreferrer"
                className="instagram-post overflow-hidden relative"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <AspectRatio ratio={1 / 1} className="w-full h-full">
                  <img
                    src={post.imageUrl}
                    alt={`Instagram post ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      // Fallback for image loading errors
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.src = post.isReel ? uploadedExampleImage : fallbackPosts[index % fallbackPosts.length].imageUrl;
                    }}
                  />
                </AspectRatio>
                
                {/* Show Reel indicator if it's a reel */}
                {post.isReel && (
                  <div className="absolute top-2 right-2">
                    <div className="bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 mr-1">
                        <path fillRule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" />
                      </svg>
                      Reel
                    </div>
                  </div>
                )}
                
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-2">
                  {!isMobile && (
                    <p className="text-white text-xs line-clamp-2 mb-1">{post.caption}</p>
                  )}
                  <div className="flex items-center text-white text-xs">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 mr-1">
                      <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
                    </svg>
                    {post.likes}
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
        
        <div className="mt-8 text-center">
          <a
            href="https://www.instagram.com/czule.klisze/"
            target="_blank"
            rel="noopener noreferrer"
            className="elegant-button-outline inline-flex items-center"
          >
            <Instagram className="mr-2 h-4 w-4" />
            Obserwuj @czule.klisze
          </a>
        </div>
      </div>
    </section>
  );
};

export default InstagramFeed;
